import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { PLAN_LIMITS, CURRENT_PLAN } from "../../constants/plan";
import { useFolderList, useCreateFolder, useDeleteFolder } from "../../queries/folders";
import { usePdfList, useDeletePdf, useRenamePdf, useToggleAutoFullscreen } from "../../queries/pdfs";
import type { PdfFileRow } from "../../db/pdfRepository";
import { useTimeAgoLocale, formatTimeAgo } from "../../hooks/useTimeAgo";
import { pdfjs } from "react-pdf";

// PDF.js worker 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FOLDER_COLORS = [
  { value: "#6366F1", label: "인디고" },
  { value: "#8B5CF6", label: "보라" },
  { value: "#EC4899", label: "핑크" },
  { value: "#EF4444", label: "빨강" },
  { value: "#F97316", label: "주황" },
  { value: "#EAB308", label: "노랑" },
  { value: "#22C55E", label: "초록" },
  { value: "#14B8A6", label: "청록" },
];

// 스켈레톤 카드 컴포넌트
const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 w-full animate-pulse">
    {/* 썸네일 영역 */}
    <div className="bg-gray-200 h-40 sm:h-44" />
    {/* 정보 영역 */}
    <div className="p-4">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
      <div className="flex items-center gap-3 mb-3">
        <div className="h-3 bg-gray-200 rounded w-12" />
        <div className="h-3 bg-gray-200 rounded w-14" />
        <div className="h-3 bg-gray-200 rounded w-16" />
      </div>
      <div className="h-8 bg-gray-100 rounded-lg mb-2" />
      <div className="flex gap-1">
        <div className="flex-1 h-8 bg-gray-100 rounded-lg" />
        <div className="flex-1 h-8 bg-gray-100 rounded-lg" />
        <div className="flex-1 h-8 bg-gray-100 rounded-lg" />
        <div className="flex-1 h-8 bg-gray-100 rounded-lg" />
        <div className="flex-1 h-8 bg-gray-100 rounded-lg" />
      </div>
    </div>
  </div>
);

// 툴팁 버튼 컴포넌트
const TooltipButton = ({
  onClick,
  label,
  icon,
  bgClass,
  textClass,
  hoverBgClass,
  hoverTextClass,
}: {
  onClick: () => void;
  label: string;
  icon: string;
  bgClass: string;
  textClass: string;
  hoverBgClass: string;
  hoverTextClass: string;
}) => (
  <div className="relative flex-1 group/tip">
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-center px-2 py-2.5 ${bgClass} ${hoverBgClass} ${textClass} ${hoverTextClass} rounded-lg transition-all cursor-pointer`}
    >
      <i className={`${icon} text-base`}></i>
    </button>
    <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-[11px] rounded-md whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none z-50">
      {label}
    </span>
  </div>
);

export default function Dashboard() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 업로드 상태
  const [pdfLoading, setPdfLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // 사이드바 상태
  const queryClient = useQueryClient();
  const { data: folders = [] } = useFolderList(user?.id ?? null);
  const createFolderMutation = useCreateFolder();
  const deleteFolderMutation = useDeleteFolder();
  const { data: pdfList = [], isLoading: pdfListLoading } = usePdfList(user?.id ?? null);
  const deletePdfMutation = useDeletePdf();
  const renamePdfMutation = useRenamePdf();
  const autoFullscreenMutation = useToggleAutoFullscreen();
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectColor, setNewProjectColor] = useState("#6366F1");
  const usedStorage = useMemo(
    () => Number((pdfList.reduce((sum, pdf) => sum + ((pdf as any).file_size || 0), 0) / (1024 * 1024)).toFixed(1)),
    [pdfList],
  );
  const maxStorage = PLAN_LIMITS[CURRENT_PLAN].maxTotalStorage;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const timeAgoLocale = useTimeAgoLocale();

  const pdfListLoaded = !pdfListLoading;

  // 검색 및 정렬 상태
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortOption, setSortOption] = useState<
    | "date_desc"
    | "date_asc"
    | "name_asc"
    | "name_desc"
    | "size_desc"
    | "size_asc"
  >("date_desc");
  const [showSortDropdown, setShowSortDropdown] = useState<boolean>(false);

  // 파일 이름 변경 상태
  const [editingPdfId, setEditingPdfId] = useState<string | null>(null);
  const [editingFileName, setEditingFileName] = useState<string>("");

  // PDF 삭제 확인 모달 상태
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    pdfId: string | null;
    pdfName: string;
  }>({
    isOpen: false,
    pdfId: null,
    pdfName: "",
  });

  /** 폴더 삭제 확인 모달 상태 */
  const [deleteFolderConfirmModal, setDeleteFolderConfirmModal] = useState<{
    isOpen: boolean;
    folderId: string | null;
    folderName: string;
  }>({
    isOpen: false,
    folderId: null,
    folderName: "",
  });

  // 필터링 및 정렬된 PDF 목록
  const filteredAndSortedPdfList = pdfList
    .filter((pdf) => {
      if (selectedProject !== null && pdf.folder_id !== selectedProject)
        return false;
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return pdf.file_name?.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      switch (sortOption) {
        case "date_desc":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "date_asc":
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case "name_asc":
          return (a.file_name || "").localeCompare(b.file_name || "", "ko");
        case "name_desc":
          return (b.file_name || "").localeCompare(a.file_name || "", "ko");
        case "size_desc":
          return (b.file_size || 0) - (a.file_size || 0);
        case "size_asc":
          return (a.file_size || 0) - (b.file_size || 0);
        default:
          return 0;
      }
    });

  // 정렬 옵션 라벨
  const sortOptionLabels: Record<string, string> = {
    date_desc: t("dashboard.sort_date_desc"),
    date_asc: t("dashboard.sort_date_asc"),
    name_asc: t("dashboard.sort_name_asc"),
    name_desc: t("dashboard.sort_name_desc"),
    size_desc: t("dashboard.sort_size_desc"),
    size_asc: t("dashboard.sort_size_asc"),
  };

  // 사용자 인증 확인
  const checkUser = async () => {
    try {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (authUser) {
        setUser(authUser);
        setLoading(false);
        return;
      }

      navigate("/login");
    } catch (err) {
      console.error("사용자 확인 오류:", err);
      navigate("/login");
    }
  };

  useEffect(() => {
    checkUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 로그아웃
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/");
    } catch (err) {
      console.error("로그아웃 오류:", err);
      navigate("/");
    }
  };

  // 자동 재생 설정 변경
  const handleAutoPlayChange = async (pdfId: string, checked: boolean) => {
    if (!user) return;
    await autoFullscreenMutation.mutateAsync({ pdfId, userId: user.id, enabled: checked });
  };

  // PDF 파일 업로드 핸들러
  const handlePdfUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    const fileSizeMB = file.size / (1024 * 1024);
    const maxFileSize = PLAN_LIMITS[CURRENT_PLAN].maxFileSize;
    if (fileSizeMB > maxFileSize) {
      alert(t("dashboard.file_too_large", { limit: maxFileSize }));
      return;
    }
    if (usedStorage + fileSizeMB > maxStorage) {
      alert(t("dashboard.storage_exceeded", { limit: maxStorage }));
      return;
    }

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "ppt" || ext === "pptx") {
      await uploadAndConvertPpt(file);
    } else if (file.type === "application/pdf" || ext === "pdf") {
      await uploadPdfToSupabase(file);
    } else {
      alert(t("dashboard.pdf_only"));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (!file || !user) return;

    const fileSizeMB = file.size / (1024 * 1024);
    const maxFileSize = PLAN_LIMITS[CURRENT_PLAN].maxFileSize;
    if (fileSizeMB > maxFileSize) {
      alert(t("dashboard.file_too_large", { limit: maxFileSize }));
      return;
    }
    if (usedStorage + fileSizeMB > maxStorage) {
      alert(t("dashboard.storage_exceeded", { limit: maxStorage }));
      return;
    }

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "ppt" || ext === "pptx") {
      await uploadAndConvertPpt(file);
    } else if (file.type === "application/pdf" || ext === "pdf") {
      await uploadPdfToSupabase(file);
    } else {
      alert(t("dashboard.pdf_only"));
    }
  };

  const uploadAndConvertPpt = async (file: File) => {
    if (!user) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    const timestamp = Date.now();
    const ext = file.name.split(".").pop() || "pptx";
    const storagePath = `${user.id}/${timestamp}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("pdfs")
      .upload(storagePath, file, { cacheControl: "3600", upsert: false });

    if (uploadError) {
      alert(t("dashboard.upload_failed", { message: uploadError.message }));
      return;
    }

    const pdfPath = storagePath.replace(/\.(ppt|pptx)$/i, ".pdf");
    const { data: newRow, error: dbError } = await supabase
      .from("pdf_files")
      .insert({
        user_id: user.id,
        file_name: file.name.replace(/\.(ppt|pptx)$/i, ".pdf"),
        file_path: pdfPath,
        original_path: storagePath,
        file_size: file.size,
        page_count: 0,
        current_page: 1,
        auto_fullscreen: false,
        status: "converting",
      })
      .select()
      .single();

    if (dbError || !newRow) {
      alert(t("dashboard.save_failed", { message: dbError?.message }));
      await supabase.storage.from("pdfs").remove([storagePath]);
      return;
    }

    queryClient.setQueryData<PdfFileRow[]>(["pdfs", user.id], (old) => [newRow as PdfFileRow, ...(old ?? [])]);

    const converterUrl = import.meta.env.VITE_CONVERTER_URL;
    const apiKey = import.meta.env.VITE_CONVERTER_API_KEY;
    fetch(`${converterUrl}/convert`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
      },
      body: JSON.stringify({ path: storagePath, id: newRow.id, jwt: session.access_token }),
    }).catch((err) => console.error("Converter call failed:", err));

    const channel = supabase
      .channel(`pdf-status-${newRow.id}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "pdf_files",
        filter: `id=eq.${newRow.id}`,
      }, (payload) => {
        const updated = payload.new as PdfFileRow;
        queryClient.setQueryData<PdfFileRow[]>(["pdfs", user.id], (old) =>
          (old ?? []).map((p) => (p.id === newRow.id ? updated : p)),
        );
        if (updated.status === "done" || updated.status === "error") {
          supabase.removeChannel(channel);
        }
      })
      .subscribe();
  };

  // Supabase Storage에 PDF 업로드
  const uploadPdfToSupabase = async (file: File) => {
    if (!user) return;

    setPdfLoading(true);
    setUploadProgress(0);

    try {
      const timestamp = Date.now();
      const fileExtension = file.name.split(".").pop() || "pdf";
      const fileName = `${user.id}/${timestamp}.${fileExtension}`;

      const { error: uploadError } = await supabase.storage
        .from("pdfs")
        .upload(fileName, file, { cacheControl: "3600", upsert: false });

      if (uploadError) {
        alert(t("dashboard.upload_failed", { message: uploadError.message }));
        setPdfLoading(false);
        return;
      }

      setUploadProgress(50);

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      const pageCount = pdf.numPages;

      setUploadProgress(60);

      // PDF.js로 첫 페이지 썸네일 생성
      let thumbnail_url: string | undefined;
      try {
        const firstPage = await pdf.getPage(1);
        const viewport = firstPage.getViewport({ scale: 0.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d")!;
        await firstPage.render({ canvasContext: ctx, viewport }).promise;

        const thumbnailBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) =>
              blob ? resolve(blob) : reject(new Error("썸네일 변환 실패")),
            "image/jpeg",
            0.8,
          );
        });

        const thumbnailPath = `${user.id}/${timestamp}.jpg`;
        const { error: thumbnailError } = await supabase.storage
          .from("thumbnails")
          .upload(thumbnailPath, thumbnailBlob, {
            contentType: "image/jpeg",
            cacheControl: "86400",
          });

        if (!thumbnailError) {
          const { data: thumbUrlData } = supabase.storage
            .from("thumbnails")
            .getPublicUrl(thumbnailPath);
          thumbnail_url = thumbUrlData.publicUrl;
        }
      } catch (thumbErr) {
        console.warn("썸네일 생성 실패 (무시):", thumbErr);
      }

      setUploadProgress(80);

      const { error: dbError } = await supabase.from("pdf_files").insert({
        user_id: user.id,
        file_name: file.name,
        file_path: fileName,
        file_size: file.size,
        page_count: pageCount,
        current_page: 1,
        auto_fullscreen: false,
        thumbnail_url,
      });

      if (dbError) {
        alert(t("dashboard.save_failed", { message: dbError.message }));
        await supabase.storage.from("pdfs").remove([fileName]);
        setPdfLoading(false);
        return;
      }

      setUploadProgress(100);
      await queryClient.invalidateQueries({ queryKey: ["pdfs"] });

      setTimeout(() => {
        setUploadProgress(0);
        setPdfLoading(false);
      }, 500);
    } catch (err) {
      console.error("업로드 오류:", err);
      alert(t("dashboard.upload_error"));
      setPdfLoading(false);
      setUploadProgress(0);
    }
  };

  // PDF 삭제
  const closePdf = async (pdfId: string) => {
    if (!user) return;

    try {
      const pdf = pdfList.find((p) => p.id === pdfId);
      if (pdf) {
        await deletePdfMutation.mutateAsync({
          pdfId,
          filePath: pdf.file_path,
          thumbnailUrl: pdf.thumbnail_url,
        });
      }

      setDeleteConfirmModal({ isOpen: false, pdfId: null, pdfName: "" });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("PDF 삭제 오류:", err);
      alert(t("dashboard.delete_error"));
    }
  };

  // 삭제 확인 모달 열기
  const openDeleteConfirm = (pdfId: string, pdfName: string) => {
    setDeleteConfirmModal({ isOpen: true, pdfId, pdfName });
  };

  // 파일 이름 변경
  const handleRenameStart = (pdf: any) => {
    setEditingPdfId(pdf.id);
    setEditingFileName(pdf.file_name.replace(".pdf", ""));
  };

  const handleRenameCancel = () => {
    setEditingPdfId(null);
    setEditingFileName("");
  };

  const handleRenameSave = async (pdfId: string) => {
    if (!editingFileName.trim()) {
      alert(t("dashboard.enter_file_name"));
      return;
    }
    try {
      const newFileName = editingFileName.trim() + ".pdf";
      await renamePdfMutation.mutateAsync({ pdfId, newFileName });
      setEditingPdfId(null);
      setEditingFileName("");
    } catch (err) {
      console.error("파일 이름 변경 오류:", err);
      alert(t("dashboard.rename_error"));
    }
  };

  // 폴더(프로젝트) 관리
  const handleAddProject = async () => {
    if (!newProjectName.trim() || !user) return;
    await createFolderMutation.mutateAsync({ userId: user.id, name: newProjectName.trim(), color: newProjectColor });
    setNewProjectName("");
    setNewProjectColor("#6366F1");
    setIsAddingProject(false);
  };

  /**
   * 폴더 삭제 확인 모달을 엽니다.
   * @param projectId - 삭제할 폴더 ID
   */
  const handleDeleteProject = (projectId: string) => {
    const folder = folders.find((f) => f.id === projectId);
    setDeleteFolderConfirmModal({
      isOpen: true,
      folderId: projectId,
      folderName: folder?.name ?? "",
    });
  };

  /** 폴더 삭제 확인 후 실제 삭제를 수행합니다. */
  const confirmDeleteFolder = async () => {
    if (!deleteFolderConfirmModal.folderId) return;
    await deleteFolderMutation.mutateAsync(deleteFolderConfirmModal.folderId);
    if (selectedProject === deleteFolderConfirmModal.folderId)
      setSelectedProject(null);
    setDeleteFolderConfirmModal({
      isOpen: false,
      folderId: null,
      folderName: "",
    });
  };

  // 파일 크기 포맷
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return Math.round(bytes / (1024 * 1024)) + " MB";
  };

  return (
    <div className="bg-gray-50 flex min-h-screen">
      {/* 삭제 확인 모달 */}
      {deleteConfirmModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() =>
              setDeleteConfirmModal({ isOpen: false, pdfId: null, pdfName: "" })
            }
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-in fade-in zoom-in duration-200">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              {t("dashboard.delete_file_confirm")}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              <span className="font-medium text-gray-700">
                {deleteConfirmModal.pdfName}
              </span>
              <br />
              {t("dashboard.delete_file_warning")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() =>
                  setDeleteConfirmModal({
                    isOpen: false,
                    pdfId: null,
                    pdfName: "",
                  })
                }
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={() =>
                  deleteConfirmModal.pdfId && closePdf(deleteConfirmModal.pdfId)
                }
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                {t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 폴더 삭제 확인 모달 */}
      {deleteFolderConfirmModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() =>
              setDeleteFolderConfirmModal({
                isOpen: false,
                folderId: null,
                folderName: "",
              })
            }
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-in fade-in zoom-in duration-200">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-folder-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              {t("dashboard.delete_folder_confirm")}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              {t("dashboard.delete_folder_name", {
                name: deleteFolderConfirmModal.folderName,
              })}
              <br />
              {t("dashboard.delete_folder_warning")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() =>
                  setDeleteFolderConfirmModal({
                    isOpen: false,
                    folderId: null,
                    folderName: "",
                  })
                }
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={confirmDeleteFolder}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                {t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 모바일 사이드바 오버레이 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 사이드바 */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-300 lg:transform-none ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* 로고 */}
        <div className="px-4 py-4 h-[73px] flex items-center justify-center">
          <img
            src="https://static.readdy.ai/image/fa9e51599f3a91cf824c0f1e50ffec3c/bbe0145b0a3abb3ea6b08059cd830213.png"
            alt="Qpresen Logo"
            className="h-12 object-contain"
          />
        </div>

        {/* 메뉴 */}
        <div className="flex-1 overflow-y-auto px-3">
          {/* 전체 프레젠테이션 */}
          <button
            onClick={() => setSelectedProject(null)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer mb-3 ${
              selectedProject === null
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <div className="flex items-center gap-2.5">
              <i className="ri-folder-line text-base"></i>
              <span className="font-medium">
                {t("dashboard.all_presentations")}
              </span>
            </div>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                selectedProject === null
                  ? "bg-indigo-100 text-indigo-600"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {pdfList.length}
            </span>
          </button>

          {/* 새 프로젝트 버튼 */}
          <button
            onClick={() => setIsAddingProject(true)}
            className="hidden sm:flex w-full items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer shadow-sm mb-4"
          >
            <i className="ri-add-line text-lg"></i>
            {t("dashboard.new_project")}
          </button>

          {/* 프로젝트 섹션 */}
          <div className="mt-2">
            <div className="flex items-center justify-between px-3 mb-2">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t("dashboard.projects")}
              </span>
            </div>
            <div className="space-y-1">
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className={`group flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer ${
                    selectedProject === folder.id
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setSelectedProject(folder.id)}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: folder.color }}
                    ></div>
                    <span>{folder.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        selectedProject === folder.id
                          ? "bg-indigo-100 text-indigo-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {pdfList.filter((p) => p.folder_id === folder.id).length}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(folder.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all cursor-pointer"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}

              {isAddingProject && (
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddProject();
                        if (e.key === "Escape") {
                          setIsAddingProject(false);
                          setNewProjectName("");
                        }
                      }}
                      placeholder={t("dashboard.project_name_placeholder")}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      autoFocus
                    />
                    <button
                      onClick={handleAddProject}
                      className="w-7 h-7 flex items-center justify-center bg-indigo-500 hover:bg-indigo-600 text-white rounded-md transition-all cursor-pointer"
                    >
                      <i className="ri-check-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingProject(false);
                        setNewProjectName("");
                        setNewProjectColor("#6366F1");
                      }}
                      className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-md transition-all cursor-pointer"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 px-1">
                    {FOLDER_COLORS.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setNewProjectColor(color.value)}
                        className={`w-5 h-5 rounded-full transition-all cursor-pointer ${
                          newProjectColor === color.value
                            ? "ring-2 ring-offset-1 ring-gray-400 scale-110"
                            : ""
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 사이드바 하단: 저장 공간 + 로그아웃 */}
        <div className="px-4 py-4 border-t border-gray-200 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500">
                {t("dashboard.storage")}
              </span>
              <span className="text-xs font-medium text-gray-700">
                {usedStorage.toFixed(1)} / {maxStorage} MB
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-indigo-500 h-1.5 rounded-full transition-all"
                style={{
                  width: `${Math.min((usedStorage / maxStorage) * 100, 100)}%`,
                }}
              ></div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all cursor-pointer"
          >
            <i className="ri-logout-box-r-line"></i>
            {t("common.logout")}
          </button>
        </div>
      </aside>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 min-w-0">
        {/* 상단 헤더 */}
        <header className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-0 flex items-center flex-shrink-0 h-[60px] sm:h-[73px] border-b border-gray-200">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              {/* 모바일 메뉴 버튼 */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-all cursor-pointer flex-shrink-0"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>

              {/* 저장 공간 표시 */}
              <div className="flex items-center gap-2">
                <i className="ri-hard-drive-2-line text-gray-400 text-sm"></i>
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs text-gray-600 leading-none">
                    {usedStorage.toFixed(1)}{" "}
                    <span className="text-gray-400">/ {maxStorage} MB</span>
                  </span>
                  <div className="w-20 sm:w-28 bg-gray-200 rounded-full h-1">
                    <div
                      className="bg-indigo-400 h-1 rounded-full transition-all"
                      style={{
                        width: `${Math.min((usedStorage / maxStorage) * 100, 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 오른쪽: 업로드 버튼 */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={pdfLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap shadow-sm"
            >
              <i className="ri-upload-cloud-2-line text-base"></i>
              <span className="hidden sm:inline">
                {t("dashboard.pdf_upload")}
              </span>
              <span className="sm:hidden">{t("dashboard.upload")}</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf,.ppt,.pptx"
              onChange={handlePdfUpload}
              className="hidden"
              disabled={pdfLoading}
            />
          </div>
        </header>

        {/* 업로드 진행률 바 */}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="bg-indigo-50 px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-indigo-200 rounded-full h-1.5">
                <div
                  className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <span className="text-xs text-indigo-600 font-medium whitespace-nowrap">
                {uploadProgress}%
              </span>
            </div>
          </div>
        )}

        {/* 콘텐츠 영역 */}
        <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* 모바일 QR 스캔 버튼 */}
          {isMobile && (
            <div className="mb-6">
              <button
                onClick={() => navigate("/qr-scanner")}
                className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-xl text-base font-medium transition-all whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-qr-scan-2-line text-2xl"></i>
                {t("dashboard.qr_scan")}
              </button>
            </div>
          )}

          {/* 검색 및 정렬 */}
          <div className="flex items-center gap-3 mb-6">
            {/* 검색 */}
            <div className="relative flex-1 max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                placeholder={t("dashboard.search_placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>

            {/* 정렬 드롭다운 */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-2 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-sort-desc text-gray-400"></i>
                  {sortOptionLabels[sortOption]}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-gray-400 transition-transform ${showSortDropdown ? "rotate-180" : ""}`}
                ></i>
              </button>

              {showSortDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowSortDropdown(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[160px] py-1">
                    {Object.entries(sortOptionLabels).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => {
                          setSortOption(key as typeof sortOption);
                          setShowSortDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-all cursor-pointer ${
                          sortOption === key
                            ? "bg-indigo-50 text-indigo-700 font-medium"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 검색 결과 안내 */}
          {searchQuery && (
            <div className="mb-4 text-sm text-gray-500">
              {t("dashboard.search_result_count", {
                count: filteredAndSortedPdfList.length,
              })}
            </div>
          )}

          {/* PDF 카드 그리드 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {/* 업로드 영역 */}
            {!loading && (
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-6 sm:p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all cursor-pointer w-full flex flex-col items-center justify-center min-h-[280px]"
                onClick={() => !pdfLoading && fileInputRef.current?.click()}
              >
                {pdfLoading ? (
                  <div className="space-y-3">
                    <div className="w-14 h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto">
                      <div className="animate-spin rounded-full h-7 w-7 border-2 border-indigo-500 border-t-transparent"></div>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {t("dashboard.uploading")}
                    </h3>
                    {uploadProgress > 0 && (
                      <p className="text-sm text-indigo-600">
                        {uploadProgress}%
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {t("dashboard.pdf_upload")}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {t("dashboard.click_or_drag")}
                    </p>
                    <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-lg transition-all whitespace-nowrap">
                      <i className="ri-folder-open-line"></i>
                      {t("dashboard.select_file")}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* PDF 카드 목록 */}
            {loading &&
              Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={`skeleton-${i}`} />
              ))}
            {!loading &&
              filteredAndSortedPdfList.map((pdf) => (
                <div
                  key={pdf.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 w-full"
                >
                  {/* PDF 썸네일 영역 */}
                  <div className="relative bg-gradient-to-br from-indigo-100 to-purple-100">
                    {pdf.status === "converting" ? (
                      <div className="flex flex-col items-center justify-center h-40 sm:h-44 bg-gradient-to-br from-indigo-50 to-purple-50">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-400 border-t-transparent mb-2"></div>
                        <p className="text-xs text-indigo-500 font-medium">변환 중...</p>
                        <p className="text-xs text-gray-400 mt-1">최대 2분 소요</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-40 sm:h-44">
                        {pdf.thumbnail_url ? (
                          <img src={pdf.thumbnail_url} alt={pdf.file_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex items-center justify-center py-6">
                            <div className="text-center">
                              <i className="ri-image-line text-4xl text-gray-300 mb-2"></i>
                              <p className="text-xs text-gray-400">{t("dashboard.no_image")}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 재생 버튼 오버레이 */}
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-all cursor-pointer group"
                      onClick={() =>
                        pdf.status !== "converting" &&
                        navigate(
                          isMobile
                            ? `/presentations/${pdf.id}/remote`
                            : `/presentations/${pdf.id}`,
                        )
                      }
                    >
                      <div className="w-10 h-10 bg-white/90 group-hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all transform group-hover:scale-110">
                        <i className="ri-play-fill text-xl text-indigo-600 ml-0.5"></i>
                      </div>
                    </div>
                  </div>

                  {/* 파일 정보 */}
                  <div className="p-4">
                    {/* 파일 이름 편집 UI */}
                    {editingPdfId === pdf.id ? (
                      <div className="mb-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingFileName}
                            onChange={(e) => setEditingFileName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleRenameSave(pdf.id);
                              if (e.key === "Escape") handleRenameCancel();
                            }}
                            className="flex-1 px-2 py-1.5 text-sm border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            autoFocus
                          />
                          <button
                            onClick={() => handleRenameSave(pdf.id)}
                            className="w-7 h-7 flex items-center justify-center bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-all cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-check-line text-sm"></i>
                          </button>
                          <button
                            onClick={handleRenameCancel}
                            className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-lg transition-all cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="flex-1 font-semibold text-gray-900 text-sm sm:text-base truncate">
                            {pdf.file_name?.replace(".pdf", "") ||
                              "Qpresen 소개자료"}
                          </h3>
                          <button
                            onClick={() => handleRenameStart(pdf)}
                            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                        </div>
                        <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {formatTimeAgo(pdf.created_at, timeAgoLocale)}
                        </div>
                      </div>
                    )}

                    <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-2 sm:gap-3 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-file-list-3-line"></i>
                        {pdf.page_count}P
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-hard-drive-2-line"></i>
                        {formatFileSize(pdf.file_size || 0)}
                      </span>
                    </p>

                    {/* 자동 재생 체크박스 */}
                    <label className="flex items-center gap-2 cursor-pointer mb-3 p-2 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={pdf.auto_fullscreen || false}
                        onChange={(e) =>
                          handleAutoPlayChange(pdf.id, e.target.checked)
                        }
                        className="w-4 h-4 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                      />
                      <span className="text-xs text-gray-700">
                        {t("dashboard.auto_play_on_login")}
                      </span>
                    </label>

                    {/* 버튼들 */}
                    <div className={`flex items-center gap-2 ${pdf.status === "converting" ? "opacity-50 pointer-events-none" : ""}`}>
                      <TooltipButton
                        onClick={() =>
                          navigate(`/dashboard/speaker-notes?id=${pdf.id}`)
                        }
                        label={t("dashboard.speaker_notes")}
                        icon="ri-sticky-note-line"
                        bgClass="bg-amber-50"
                        textClass="text-amber-500"
                        hoverBgClass="hover:bg-amber-100"
                        hoverTextClass="hover:text-amber-600"
                      />
                      <TooltipButton
                        onClick={() =>
                          navigate(`/dashboard/share?id=${pdf.id}`)
                        }
                        label={t("dashboard.share")}
                        icon="ri-share-line"
                        bgClass="bg-teal-50"
                        textClass="text-teal-500"
                        hoverBgClass="hover:bg-teal-100"
                        hoverTextClass="hover:text-teal-600"
                      />
                      <TooltipButton
                        onClick={() =>
                          navigate(`/dashboard/folder?id=${pdf.id}`)
                        }
                        label={t("dashboard.folder")}
                        icon="ri-folder-transfer-line"
                        bgClass="bg-indigo-50"
                        textClass="text-indigo-500"
                        hoverBgClass="hover:bg-indigo-100"
                        hoverTextClass="hover:text-indigo-600"
                      />
                      <div className="pointer-events-auto opacity-100">
                        <TooltipButton
                          onClick={() => openDeleteConfirm(pdf.id, pdf.file_name)}
                          label={t("common.delete")}
                          icon="ri-delete-bin-line"
                          bgClass="bg-red-50"
                          textClass="text-red-400"
                          hoverBgClass="hover:bg-red-100"
                          hoverTextClass="hover:text-red-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* 빈 상태 */}
          {!loading &&
            pdfListLoaded &&
            filteredAndSortedPdfList.length === 0 &&
            !pdfLoading && (
              <div className="col-span-full flex flex-col items-center justify-center py-16 text-gray-400">
                <i className="ri-file-pdf-line text-5xl mb-3"></i>
                {searchQuery ? (
                  <p className="text-sm">{t("dashboard.no_search_results")}</p>
                ) : (
                  <>
                    <p className="text-sm">{t("dashboard.no_presentations")}</p>
                    <p className="text-xs mt-1">
                      {t("dashboard.no_presentations_hint")}
                    </p>
                  </>
                )}
              </div>
            )}
        </div>
      </main>
    </div>
  );
}
